import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from "lucide-react";

// Service Points admin — the named, sectioned dining tree (channel -> area -> table). Lives
// inside Settings' Ordering section. Mutations hit the API immediately (like Staff / Stamp
// management lists), so this is NOT part of the Ordering section's single-Save form.
export default function ServicePointsManager() {
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState({}); // { [areaId]: { count, prefix } }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-points");
      setFlat(data.service_points || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const childrenOf = useCallback((pid) =>
    flat.filter((s) => s.parent_id === pid)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.label).localeCompare(String(b.label))),
    [flat]);

  const run = useCallback(async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  }, [load]);

  const addChannel = () => {
    const label = window.prompt("Channel name (e.g. Dine-in, Takeaway, Rooms)");
    if (!label || !label.trim()) return;
    const ot = window.prompt("Order type for this channel: dine_in / takeaway / room / delivery", "dine_in");
    if (!ot || !ot.trim()) return;
    run(() => api.post("/service-points", { label: label.trim(), level: "channel", order_type: ot.trim() }));
  };
  const addArea = (channel) => {
    const label = window.prompt(`New area under "${channel.label}"`);
    if (!label || !label.trim()) return;
    run(() => api.post("/service-points", { label: label.trim(), level: "area", parent_id: channel.id }));
  };
  const addTable = (area) => {
    const label = window.prompt(`New table under "${area.label}"`);
    if (!label || !label.trim()) return;
    run(() => api.post("/service-points", { label: label.trim(), level: "table", parent_id: area.id }));
  };
  const bulkAdd = (area) => {
    const b = bulk[area.id] || { count: 10, prefix: "" };
    const count = Number(b.count);
    if (!(count >= 1 && count <= 200)) { toast.error("Count must be 1–200"); return; }
    run(async () => {
      await api.post("/service-points/bulk-tables", { area_id: area.id, count, prefix: (b.prefix || "").trim() });
      setBulk((p) => ({ ...p, [area.id]: { count: 10, prefix: "" } }));
    });
  };
  const rename = (sp) => {
    const label = window.prompt("Rename", sp.label);
    if (!label || !label.trim() || label.trim() === sp.label) return;
    run(() => api.put(`/service-points/${sp.id}`, { label: label.trim() }));
  };
  const toggleActive = (sp) => run(() => api.put(`/service-points/${sp.id}`, { active: !sp.active }));
  const del = (sp) => {
    if (!window.confirm(`Delete "${sp.label}"? If it has ever been used on a bill, deactivate it instead.`)) return;
    run(() => api.delete(`/service-points/${sp.id}`));
  };
  // Reorder by swapping sort_order with the adjacent sibling.
  const move = (sp, dir) => {
    const sibs = childrenOf(sp.parent_id);
    const i = sibs.findIndex((s) => s.id === sp.id);
    const j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    const a = sibs[i], b = sibs[j];
    run(async () => {
      await api.put(`/service-points/${a.id}`, { sort_order: b.sort_order ?? j });
      await api.put(`/service-points/${b.id}`, { sort_order: a.sort_order ?? i });
    });
  };

  const iconBtn = (onClick, title, children) => (
    <button type="button" disabled={busy} onClick={onClick} title={title}
      className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 border border-white/10 text-[#A0A0A0] hover:text-white hover:bg-white/10 disabled:opacity-40">
      {children}
    </button>
  );

  if (loading) return <p className="text-xs text-[#A0A0A0]">Loading service points…</p>;

  const channels = childrenOf(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Service points (tables & areas)</div>
        <button type="button" disabled={busy} onClick={addChannel}
          className="text-[11px] font-semibold text-[#9D00FF] hover:text-[#B94DFF] flex items-center gap-1 disabled:opacity-40">
          <Plus size={13} /> Add channel
        </button>
      </div>

      {channels.map((ch) => (
        <div key={ch.id} className="bg-[#0F0F0F] border border-white/5 rounded-xl p-3">
          <div className={`flex items-center gap-2 ${ch.active ? "" : "opacity-40"}`}>
            <span className="text-sm font-bold text-white">{ch.label}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#666]">{ch.order_type}</span>
            <div className="ml-auto flex items-center gap-1">
              {iconBtn(() => addArea(ch), "Add area", <Plus size={13} />)}
              {iconBtn(() => rename(ch), "Rename", <Pencil size={12} />)}
              {iconBtn(() => toggleActive(ch), ch.active ? "Deactivate" : "Activate", <span className="text-[10px] font-bold">{ch.active ? "On" : "Off"}</span>)}
              {iconBtn(() => del(ch), "Delete", <Trash2 size={12} />)}
            </div>
          </div>

          {childrenOf(ch.id).map((area) => (
            <div key={area.id} className="mt-2 ml-3 pl-3 border-l border-white/5">
              <div className={`flex items-center gap-2 ${area.active ? "" : "opacity-40"}`}>
                <span className="text-sm font-semibold text-white">{area.label}</span>
                <div className="ml-auto flex items-center gap-1">
                  {iconBtn(() => move(area, -1), "Move up", <ChevronUp size={13} />)}
                  {iconBtn(() => move(area, 1), "Move down", <ChevronDown size={13} />)}
                  {iconBtn(() => addTable(area), "Add table", <Plus size={13} />)}
                  {iconBtn(() => rename(area), "Rename", <Pencil size={12} />)}
                  {iconBtn(() => toggleActive(area), area.active ? "Deactivate" : "Activate", <span className="text-[10px] font-bold">{area.active ? "On" : "Off"}</span>)}
                  {iconBtn(() => del(area), "Delete", <Trash2 size={12} />)}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-2">
                {childrenOf(area.id).map((t) => (
                  <button key={t.id} type="button" disabled={busy}
                    onClick={() => rename(t)}
                    onContextMenu={(e) => { e.preventDefault(); toggleActive(t); }}
                    title={t.active ? "Click to rename · right-click to deactivate" : "Deactivated · right-click to reactivate"}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${t.active
                      ? "bg-[#9D00FF]/15 border-[#9D00FF]/30 text-white"
                      : "bg-white/5 border-white/10 text-[#555] line-through"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Bulk add tables */}
              <div className="flex items-center gap-2 mt-2">
                <input type="number" min={1} max={200} placeholder="Count"
                  value={(bulk[area.id]?.count) ?? ""}
                  onChange={(e) => setBulk((p) => ({ ...p, [area.id]: { ...(p[area.id] || {}), count: e.target.value } }))}
                  className="w-16 bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-white text-xs focus:border-[#9D00FF] focus:outline-none" />
                <input type="text" placeholder="Prefix (optional)" maxLength={6}
                  value={(bulk[area.id]?.prefix) ?? ""}
                  onChange={(e) => setBulk((p) => ({ ...p, [area.id]: { ...(p[area.id] || {}), prefix: e.target.value } }))}
                  className="w-28 bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-white text-xs focus:border-[#9D00FF] focus:outline-none" />
                <button type="button" disabled={busy} onClick={() => bulkAdd(area)}
                  className="text-[11px] font-semibold text-[#9D00FF] hover:text-[#B94DFF] disabled:opacity-40">+ Add tables in bulk</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      <p className="text-[10px] text-[#666]">Deactivated items are greyed and never shown to guests, but keep their history. A table that has been used on a bill can only be deactivated, not deleted.</p>
    </div>
  );
}
